import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ownerApi } from "@/lib/api";
import { money } from "@/lib/format";
import { STATUS_META } from "@/lib/status";
import { Spinner } from "@/components/common/States";

const PERIODS = [
  { key: "7", label: "Last 7 days" },
  { key: "30", label: "30 days" },
  { key: "90", label: "90 days" },
  { key: "all", label: "All time" },
];
const CATEGORIES = ["all", "Dry Clean", "Wash n Fold", "Press", "Shoe & Accessories Clean", "Tailoring"];
const SPEEDS = ["all", "standard", "priority", "express", "rapid"];

const label = (s) => STATUS_META[s]?.label || s;
const dur = (h) => {
  if (!h) return "—";
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 48) return `${h.toFixed(1)} h`;
  return `${(h / 24).toFixed(1)} d`;
};

export default function OwnerOperations() {
  const [period, setPeriod] = useState("7");
  const [category, setCategory] = useState("all");
  const [speed, setSpeed] = useState("all");
  const query = useQuery({ queryKey: ["ownerOperations", period, category, speed], queryFn: () => ownerApi.getOperations(period, category, speed) });
  const data = query.data;
  const maxOrders = Math.max(1, ...(data?.series ?? []).map((s) => s.orders));

  return (
    <div className="space-y-4" data-testid="owner-operations">
      <div>
        <h2 className="font-display text-lg font-bold">Operations · cycle time</h2>
        <p className="text-sm text-muted-foreground">How long orders take at each stage, so you can plan staffing and set delivery promises.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="owner-ops-period" className="rounded-full border border-border bg-background px-3 py-2 text-xs font-medium">
          {PERIODS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
        <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="owner-ops-category" className="rounded-full border border-border bg-background px-3 py-2 text-xs font-medium">
          {CATEGORIES.map((c) => <option key={c} value={c}>{c === "all" ? "All categories" : c}</option>)}
        </select>
        <select value={speed} onChange={(e) => setSpeed(e.target.value)} data-testid="owner-ops-speed" className="rounded-full border border-border bg-background px-3 py-2 text-xs font-medium">
          {SPEEDS.map((s) => <option key={s} value={s}>{s === "all" ? "All speeds" : s}</option>)}
        </select>
      </div>

      {query.isPending ? <Spinner /> : !data ? null : (
        <>
          <p className="text-xs text-muted-foreground" data-testid="owner-ops-sample">Based on {data.sample_size} order{data.sample_size === 1 ? "" : "s"} in this period.</p>

          <div className="rounded-2xl border border-primary bg-primary/5 p-4" data-testid="owner-ops-overall">
            <p className="text-xs font-semibold text-muted-foreground">Placed → Completed (total cycle)</p>
            <div className="mt-2 grid grid-cols-4 gap-2 text-center">
              <div><p className="font-display text-lg font-bold">{dur(data.overall.median)}</p><p className="text-[11px] text-muted-foreground">Median</p></div>
              <div><p className="font-display text-lg font-bold">{dur(data.overall.average)}</p><p className="text-[11px] text-muted-foreground">Average</p></div>
              <div><p className="font-display text-lg font-bold">{dur(data.overall.p90)}</p><p className="text-[11px] text-muted-foreground">Slowest (p90)</p></div>
              <div><p className="font-display text-lg font-bold">{data.overall.count}</p><p className="text-[11px] text-muted-foreground">Orders</p></div>
            </div>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-left text-xs text-muted-foreground">
                <tr><th className="p-3">Stage transition</th><th className="p-3">Median</th><th className="p-3">Average</th><th className="p-3">Slowest</th><th className="p-3">Orders</th></tr>
              </thead>
              <tbody data-testid="owner-ops-transitions">
                {data.transitions.map((t) => (
                  <tr key={`${t.from}-${t.to}`} className="border-t border-border" data-testid={`owner-ops-row-${t.from}-${t.to}`}>
                    <td className="p-3 font-medium">{label(t.from)} → {label(t.to)}</td>
                    <td className="p-3">{dur(t.median)}</td>
                    <td className="p-3">{dur(t.average)}</td>
                    <td className="p-3">{dur(t.p90)}</td>
                    <td className="p-3 text-muted-foreground">{t.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rounded-2xl border border-border p-4">
            <p className="mb-3 text-sm font-semibold">Last 7 days · orders &amp; revenue</p>
            <div className="flex items-end justify-between gap-2" data-testid="owner-ops-chart">
              {data.series.map((s) => (
                <div key={s.date} className="flex flex-1 flex-col items-center gap-1">
                  <span className="text-[10px] font-semibold">{s.orders}</span>
                  <div className="flex w-full items-end justify-center">
                    <div className="w-6 rounded-t bg-primary" style={{ height: `${Math.max(4, (s.orders / maxOrders) * 90)}px` }} title={`${s.orders} orders · ${money(s.revenue)}`} />
                  </div>
                  <span className="text-[10px] text-muted-foreground">{s.date.slice(5)}</span>
                  <span className="text-[9px] text-muted-foreground">{money(s.revenue)}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
